/**
 * Where each resident lives on the painting, and how big they look there.
 *
 * Ported from apps/display/public/wildlife-layout.js. The route coordinates
 * are FRACTIONS OF THE PAINTING, traced against the actual artwork -- a duck's
 * path follows the water, a squirrel's follows a trunk -- and they only line
 * up because `camera` fits the painting the same way the CSS does:
 * `object-fit: cover`, centred. Change one and the other has to follow, or
 * every duck swims across grass.
 *
 * THESE PATHS ARE TIED TO THE CURRENT PAINTINGS. A repainted valley with the
 * stream somewhere else needs its routes retraced here; the manifest can swap
 * the art, but it cannot know where the water went.
 */

import { ATLAS, type Species } from './atlas';
import { HABITAT } from './habitat.generated';
import type { Point, Route } from './motion';

export type Habitat = 'meadow' | 'tree' | 'water' | 'bank' | 'flowers' | 'sky';

/**
 * [species, habitat, threshold, landscape path].
 *
 * `threshold` is the valley's health (0..1, see ecology.ts) above which this
 * resident appears. A barren valley has nobody; residents arrive one by one
 * as the family saves, which is the whole reward loop.
 *
 * ORDER MATTERS. An index is a resident's identity: it seeds their timing
 * offset, keys the phone paths below, and picks the three bird sizes. Append
 * new residents; never insert.
 */
export const RESIDENTS: ReadonlyArray<readonly [Species, Habitat, number, ReadonlyArray<readonly [number, number]>]> = [
  ['deer', 'meadow', 0.08, [[0.16, 0.634], [0.27, 0.664], [0.38, 0.684]]],
  ['fox', 'meadow', 0.14, [[0.17, 0.70], [0.31, 0.715], [0.46, 0.735]]],
  ['squirrel', 'tree', 0.18, [[0.347, 0.525], [0.354, 0.445], [0.368, 0.377], [0.411, 0.356]]],
  ['duck', 'water', 0.12, [[0.603, 0.582], [0.622, 0.588], [0.64, 0.587]]],
  ['frog', 'bank', 0.08, [[0.55, 0.718], [0.565, 0.735], [0.579, 0.749]]],
  ['rabbit', 'meadow', 0.12, [[0.242, 0.814], [0.322, 0.804], [0.412, 0.834]]],
  ['butterfly', 'flowers', 0.10, [[0.16, 0.59], [0.21, 0.63], [0.25, 0.61]]],
  ['raccoon', 'meadow', 0.20, [[0.36, 0.79], [0.47, 0.80], [0.58, 0.84]]],
  ['bird', 'sky', 0.12, [[0.411, 0.356], [0.60, 0.33], [0.88, 0.20]]],
  ['deer', 'meadow', 0.46, [[0.068, 0.836], [0.158, 0.866], [0.238, 0.886]]],
  ['deer', 'meadow', 0.33, [[0.401, 0.539], [0.456, 0.549], [0.506, 0.564]]],
  ['fox', 'meadow', 0.50, [[0.896, 0.519], [0.938, 0.54], [0.98, 0.564]]],
  ['raccoon', 'meadow', 0.56, [[0.16, 0.542], [0.23, 0.572], [0.29, 0.592]]],
  ['squirrel', 'tree', 0.48, [[0.102, 0.465], [0.075, 0.34], [0.084, 0.245], [0.15, 0.20]]],
  ['duck', 'water', 0.34, [[0.726, 0.775], [0.782, 0.802], [0.85, 0.819]]],
  ['duck', 'water', 0.58, [[0.786, 0.948], [0.857, 0.967], [0.935, 0.969]]],
  ['frog', 'bank', 0.30, [[0.646, 0.844], [0.65, 0.866], [0.663, 0.89]]],
  ['rabbit', 'meadow', 0.40, [[0.4, 0.6], [0.45, 0.635], [0.51, 0.65]]],
  ['butterfly', 'flowers', 0.22, [[0.44, 0.76], [0.49, 0.71], [0.54, 0.73]]],
  ['butterfly', 'flowers', 0.36, [[0.883, 0.48], [0.928, 0.53], [0.953, 0.49]]],
  ['butterfly', 'flowers', 0.48, [[0.19, 0.86], [0.24, 0.84], [0.31, 0.91]]],
  ['butterfly', 'flowers', 0.62, [[0.542, 0.588], [0.512, 0.628], [0.572, 0.648]]],
  ['bird', 'sky', 0.36, [[0.29, 0.30], [0.50, 0.33], [0.75, 0.28]]],
  ['bird', 'sky', 0.57, [[0.35, 0.13], [0.57, 0.18], [0.79, 0.12]]],
  ['owl', 'sky', 0.10, [[0.411, 0.356], [0.63, 0.31], [0.89, 0.22]]],
  ['bat', 'sky', 0.12, [[0.16, 0.18], [0.52, 0.36], [0.87, 0.19]]],
  ['hedgehog', 'meadow', 0.08, [[0.226, 0.81], [0.336, 0.83], [0.466, 0.86]]],
  ['moth', 'flowers', 0.10, [[0.478, 0.672], [0.548, 0.632], [0.588, 0.662]]],
  ['hedgehog', 'meadow', 0.48, [[0.36, 0.588], [0.43, 0.618], [0.49, 0.638]]],
  ['moth', 'flowers', 0.42, [[0.18, 0.83], [0.24, 0.79], [0.30, 0.84]]],
];

/**
 * Paths on the PORTRAIT paintings, by resident index. A resident with no
 * entry here stays home on a phone -- the portrait painting has less room,
 * and not every path has a place to go.
 */
const PHONE: Readonly<Record<number, ReadonlyArray<readonly [number, number]>>> = {
  0: [[0.188, 0.646], [0.348, 0.636], [0.488, 0.656]],
  1: [[0.08, 0.742], [0.29, 0.754], [0.54, 0.764]],
  2: [[0.08, 0.33], [0.105, 0.275], [0.19, 0.237], [0.285, 0.215]],
  3: [[0.738, 0.724], [0.778, 0.731], [0.808, 0.738]],
  4: [[0.55, 0.695], [0.57, 0.72], [0.61, 0.735]],
  5: [[0.094, 0.858], [0.234, 0.838], [0.394, 0.868]],
  6: [[0.18, 0.72], [0.25, 0.76], [0.31, 0.70]],
  7: [[0.35, 0.837], [0.48, 0.852], [0.61, 0.862]],
  8: [[0.20, 0.22], [0.45, 0.25], [0.71, 0.20]],
  10: [[0.344, 0.591], [0.434, 0.596], [0.504, 0.596]],
  16: [[0.59, 0.865], [0.60, 0.88], [0.65, 0.90]],
  18: [[0.37, 0.89], [0.44, 0.85], [0.49, 0.90]],
  24: [[0.285, 0.215], [0.56, 0.39], [0.95, 0.20]],
  25: [[0.17, 0.275], [0.53, 0.32], [0.94, 0.18]],
  26: [[0.318, 0.826], [0.428, 0.846], [0.578, 0.866]],
  27: [[0.40, 0.67], [0.50, 0.65], [0.59, 0.69]],
  29: [[0.33, 0.85], [0.42, 0.82], [0.48, 0.86]],
};

export interface Camera {
  portrait: boolean;
  /** The painting's drawn size, and where its top-left corner lands. */
  width: number;
  height: number;
  x: number;
  y: number;
}

/**
 * How the painting is fitted to a box: `cover`, centred.
 *
 * The source sizes are the display's reference sizes, NOT the pixel sizes of
 * the files -- the portrait painting is 941x1672 on disk and 900x1600 here.
 * Only the aspect ratio reaches the maths (the two differ by 0.05%), and the
 * routes were traced against these numbers.
 */
export function camera(width: number, height: number): Camera {
  const portrait = width < 700 && height > width;
  const sourceWidth = portrait ? 900 : 1672;
  const sourceHeight = portrait ? 1600 : 941;
  const scale = Math.max(width / sourceWidth, height / sourceHeight);
  return {
    portrait,
    width: sourceWidth * scale,
    height: sourceHeight * scale,
    x: (width - sourceWidth * scale) / 2,
    y: (height - sourceHeight * scale) / 2,
  };
}

/** Nearer the bottom of the painting is nearer the viewer, so bigger. */
export function perspective(y: number): number {
  return Math.max(0.38, Math.min(1.35, 0.43 + (y - 0.5) * 2.15));
}

export interface Placed extends Route {
  enabled: true;
  name: Species;
  index: number;
  /** The animal's painted height, in pixels, on this screen. */
  paintedHeight: number;
  /** The cell size that gives that height: height / fill. */
  size: number;
  habitat: Habitat;
  threshold: number;
  /** The route's representative depth, for the perspective ratio. */
  depth: number;
  /** How the painting is fitted on this screen, for checking ground. */
  view: Camera;
}

export type Arranged = Placed | { enabled: false; name: Species; index: number };

// ---------------------------------------------------------------------------
// What the painting is, under a point (2026-10-13)
// ---------------------------------------------------------------------------

/**
 * The habitat map's cell under a screen point: 'G' open meadow, 'R' rock,
 * flowers or trunk, 'W' the stream, '.' the far landscape and the sky --
 * and anything off the painting. See tools/build-wildlife-habitat.py.
 */
export function groundAt(view: Camera, p: Point): string {
  const map = view.portrait ? HABITAT.portrait : HABITAT.landscape;
  const fx = (p.x - view.x) / view.width;
  const fy = (p.y - view.y) / view.height;
  if (!(fx >= 0 && fx < 1 && fy >= 0 && fy < 1)) return '.';
  return map.cells[Math.floor(fy * map.rows) * map.cols + Math.floor(fx * map.cols)]!;
}

/**
 * Whether a route sits on its own kind of ground, on the painting as it is
 * actually drawn. Nothing that walks or swims is ever over the far
 * landscape or the sky; meadow animals rest on grass and keep to it; ducks
 * keep to the water; frogs stay out of it; butterflies and moths may cross
 * the stream but do not settle on it. Trees and the sky are checked by
 * their own rules (the trunk tracing, and perches).
 */
export function fits(view: Camera, habitat: Habitat, points: readonly Point[]): boolean {
  if (habitat === 'sky' || habitat === 'tree') return true;
  const trail = samples(points).map(({ p }) => groundAt(view, p));
  if (trail.includes('.')) return false;
  const ends = [groundAt(view, points[0]!), groundAt(view, points[points.length - 1]!)];
  const share = (cell: string) => trail.filter((c) => c === cell).length / trail.length;
  if (habitat === 'meadow') return ends.every((c) => c === 'G') && share('G') >= 0.9;
  if (habitat === 'water') return ends.every((c) => c === 'W') && share('W') >= 0.9;
  if (habitat === 'bank') return !trail.includes('W');
  return ends.every((c) => c !== 'W');
}

/** Far enough inside the screen that the whole animal shows, feet at `p`. */
const inView = (p: Point, painted: number, width: number, height: number) =>
  p.x >= painted * 0.6 && p.x <= width - painted * 0.6 && p.y >= painted && p.y <= height;

/**
 * The part of a route that is on screen.
 *
 * A painting fitted to a tablet or a card shows only part of itself, and a
 * route traced across the whole valley used to walk its animal out of the
 * frame to rest where nobody could see it. Now it keeps the longest stretch
 * in view; a stretch too short to walk is a place to stay, and a route with
 * none in view leaves its resident at home.
 */
function visiblePart(points: Point[], painted: number, width: number, height: number): Point[] | null {
  if (points.every((p) => inView(p, painted, width, height))) {
    const trail = samples(points);
    if (trail.every(({ p }) => inView(p, painted, width, height))) return points;
  }
  const trail = samples(points);
  let best: [number, number] | null = null;
  let start = -1;
  for (let i = 0; i <= trail.length; i++) {
    const shown = i < trail.length && inView(trail[i]!.p, painted, width, height);
    if (shown && start < 0) start = i;
    if (!shown && start >= 0) {
      if (!best || i - 1 - start > best[1] - best[0]) best = [start, i - 1];
      start = -1;
    }
  }
  if (!best) return null;
  const cut = best[1] > best[0] ? slice(points, trail, best[0], best[1]) : null;
  if (cut && length(cut) >= painted * 1.2) return cut;
  const stay = trail[Math.round((best[0] + best[1]) / 2)]!.p;
  return [stay, { x: stay.x + 1, y: stay.y }];
}

/**
 * A cropped route whose visible stretch no longer sits on its own ground
 * (the crop cut it short on a boulder, say): the resident stays at the
 * nearest spot to the middle that does, or stays home.
 */
function settle(view: Camera, habitat: Habitat, points: Point[]): Point[] | null {
  const trail = samples(points);
  const middle = (trail.length - 1) / 2;
  const order = trail.map((_, i) => i).sort((a, b) => Math.abs(a - middle) - Math.abs(b - middle) || a - b);
  for (const i of order) {
    const p = trail[i]!.p;
    const still = [p, { x: p.x + 1, y: p.y }];
    if (fits(view, habitat, still)) return still;
  }
  return null;
}

/**
 * A flight from one tree to another, chosen for THIS screen.
 *
 * Birds, owls and bats rest at both ends of their route, so both ends have
 * to be in a tree -- a traced end that landed on open sky left a bird
 * hovering there. They land on the canopy perches of the habitat map that
 * are on screen: the one nearest the traced start, then the one nearest the
 * traced end that is a real flight away. Perches already taken by another
 * flyer out at the same time of day are passed over while there are others,
 * so two birds do not share one twig. Fewer than two perches in view and
 * the flyer stays home.
 */
function flight(
  view: Camera,
  width: number,
  height: number,
  path: readonly Point[],
  painted: number,
  taken: Set<string>,
): Point[] | null {
  const map = view.portrait ? HABITAT.portrait : HABITAT.landscape;
  const shown = map.perches
    .map(([x, y]) => ({ x: view.x + x * view.width, y: view.y + y * view.height }))
    .filter((p) => p.x >= painted && p.x <= width - painted && p.y >= painted * 1.2 && p.y <= height - painted);
  if (shown.length < 2) return null;
  const key = (p: Point) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
  const free = shown.filter((p) => !taken.has(key(p)));
  const pool = free.length >= 2 ? free : shown;
  const gap = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
  const nearest = (to: Point, list: Point[]) => list.reduce((best, p) => (gap(p, to) < gap(best, to) ? p : best));
  const a = nearest(path[0]!, pool);
  const rest = pool.filter((p) => p !== a);
  const away = rest.filter((p) => gap(p, a) >= Math.max(width * 0.2, painted * 4));
  const b = away.length
    ? nearest(path[path.length - 1]!, away)
    : rest.reduce((best, p) => (gap(p, a) > gap(best, a) ? p : best));
  taken.add(key(a));
  taken.add(key(b));
  const top = Math.min(a.y, b.y) - Math.max(painted, height * 0.06);
  return [a, { x: (a.x + b.x) / 2, y: Math.max(painted * 1.2, top) }, b];
}

export function arrange(width: number, height: number): Arranged[] {
  const view = camera(width, height);
  const unit = Math.max(0.85, Math.min(1.5, height / 941));
  const perched = { day: new Set<string>(), night: new Set<string>() };

  return RESIDENTS.map(([name, habitat, threshold, landscape], index): Arranged => {
    const path = view.portrait ? PHONE[index] : landscape;
    if (!path) return { enabled: false, name, index };

    const rig = ATLAS[name];
    const traced: Point[] = path.map(([x, y]) => ({
      x: view.x + x * view.width,
      y: view.y + y * view.height,
    }));
    const depth = habitat === 'tree' ? 0.66 : habitat === 'sky' ? 0.53 : Math.max(...path.map((p) => p[1]));

    let painted = rig.height * perspective(depth) * unit;
    if (name === 'frog') painted = Math.max(view.portrait ? 39 : 32, painted);
    if (name === 'bird') painted = (index === 8 ? 30 : index === 22 ? 20 : 16) * unit;
    if (name === 'owl') painted = 69 * unit;
    if (name === 'bat') painted = 44 * unit;

    const points =
      habitat === 'sky'
        ? flight(view, width, height, traced, painted, rig.nocturnal ? perched.night : perched.day)
        : visiblePart(traced, painted, width, height);
    const placedAt = points && habitat !== 'sky' && !fits(view, habitat, points) ? settle(view, habitat, points) : points;
    if (!placedAt) return { enabled: false, name, index };

    return {
      enabled: true,
      name,
      index,
      points: placedAt,
      paintedHeight: painted,
      height: painted,
      size: painted / rig.fill,
      speed: rig.speed * unit * (habitat === 'sky' ? 1 : perspective(depth)),
      offset: index * 7.173,
      tempo: 1 + (index % 3) * 0.23,
      habitat,
      // On a phone the later residents arrive later still: a portrait screen
      // is small and crowds quickly, so daytime newcomers past the first
      // nine need a healthier valley before they show.
      threshold:
        view.portrait && index >= 9 && !rig.nocturnal
          ? Math.max(threshold, 0.48 + (index % 3) * 0.08)
          : threshold,
      depth,
      view,
    };
  });
}

/**
 * Whether a species is out at this time of day.
 *
 * Night creatures only at night. Everyone else only by day -- except the
 * fox, the raccoon and the frog, who are about at both.
 */
export function activeAt(name: Species, night: boolean): boolean {
  if (ATLAS[name].nocturnal) return night;
  return !night || name === 'fox' || name === 'raccoon' || name === 'frog';
}

// ---------------------------------------------------------------------------
// Keeping out of each other's way (2026-10-12)
// ---------------------------------------------------------------------------

/** A rectangle on screen: left, top, right, bottom. */
export type Box = readonly [number, number, number, number];

const hit = (a: Box, b: Box) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

/**
 * Two resting animals that would read as one on top of the other: their
 * bodies share more than a third of the smaller one. Merely touching -- a
 * rabbit beside a deer -- is a valley, not a pile.
 */
function piled(a: Box, b: Box): boolean {
  const ix = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]));
  const iy = Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
  const area = (box: Box) => (box[2] - box[0]) * (box[3] - box[1]);
  return ix * iy > Math.min(area(a), area(b)) * 0.33;
}

/** The ground an animal covers standing at `p`: feet at the point. */
function body(p: Point, painted: number): Box {
  const half = painted * 0.6 + 4;
  return [p.x - half, p.y - painted - 6, p.x + half, p.y + painted * 0.08 + 2];
}

/** Points every few pixels along a path, with the vertex index before each. */
function samples(points: readonly Point[], every = 4): Array<{ p: Point; seg: number }> {
  const out: Array<{ p: Point; seg: number }> = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!, b = points[i + 1]!;
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / every));
    for (let k = 0; k < n; k++) out.push({ p: { x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n }, seg: i });
  }
  out.push({ p: points[points.length - 1]!, seg: points.length - 2 });
  return out;
}

const length = (points: readonly Point[]) =>
  points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p.x - points[i]!.x, p.y - points[i]!.y), 0);

/**
 * WHO MAY GO WHERE, with the children in the valley.
 *
 * The routes in RESIDENTS were traced against an empty painting, and the
 * children were added later at stations of their own. So a duck rested in a
 * fishing child's lap, a fox walked through the child petting a rabbit, and on
 * a phone a frog sat on top of a resting fox.
 *
 * The rules, applied in resident order to the residents who are out:
 *   - no resident walks or rests on a child (`keepOut`): the route is cut back
 *     to its longest stretch clear of every child -- the same path, shorter,
 *     so a duck stays on its water and a squirrel on its trunk -- and if no
 *     stretch is long enough to be worth walking, that resident stays home
 *     until the children move on;
 *   - no resident stops where an earlier one stops: the ends of the route are
 *     pulled in until its rests are clear. Walking past a resting neighbour is
 *     fine; sitting on one is not.
 * Birds, owls and bats fly high and are left alone.
 *
 * Pure, and deterministic for the same inputs, so every screen agrees.
 * Mirrored by `clear` in apps/display/public/wildlife-layout.js.
 */
export function clear(arranged: readonly Arranged[], keepOut: readonly Box[], out: (index: number) => boolean): Arranged[] {
  const result: Arranged[] = arranged.map((one) => {
    if (!one.enabled || !out(one.index) || one.habitat === 'sky') return one;
    const painted = one.paintedHeight;
    const blockedAt = (p: Point) => keepOut.some((k) => hit(body(p, painted), k));
    if (!one.points.some(blockedAt) && !samples(one.points).some(({ p }) => blockedAt(p))) return one;
    // On open grass the same walk a little higher or lower up the slope is
    // still a walk on grass. Water, banks and trunks stay exactly where
    // they were painted.
    //
    // ONLY onto grass that is really there (2026-10-13). Moving up the slope
    // without looking is how a deer ended up resting on the far mountains
    // when a child sat in its meadow: the walk was shifted into whatever the
    // painting showed above it. A move now has to pass `fits` on the
    // painting and stay on screen, and down the slope is tried first.
    if (one.habitat === 'meadow' || one.habitat === 'flowers') {
      for (const k of [1, -1, 2, -2]) {
        const dy = k * painted * 0.9;
        const moved = one.points.map((p) => ({ x: p.x, y: p.y + dy }));
        // The camera is centred, so the screen is the painting plus twice its offset.
        if (!moved.every((p) => inView(p, painted, one.view.width + 2 * one.view.x, one.view.height + 2 * one.view.y))) continue;
        if (!fits(one.view, one.habitat, moved)) continue;
        if (!samples(moved).some(({ p }) => blockedAt(p))) return { ...one, points: moved };
      }
    }
    const trail = samples(one.points);
    const blocked = trail.map(({ p }) => blockedAt(p));
    // The longest stretch clear of every child.
    let best: [number, number] | null = null;
    let start = -1;
    for (let i = 0; i <= trail.length; i++) {
      const free = i < trail.length && !blocked[i];
      if (free && start < 0) start = i;
      if (!free && start >= 0) {
        if (!best || i - 1 - start > best[1] - best[0]) best = [start, i - 1];
        start = -1;
      }
    }
    if (!best) return { enabled: false, name: one.name, index: one.index } as Arranged;
    const cut = slice(one.points, trail, best[0], best[1]);
    if (cut && length(cut) >= Math.max(painted * 1.2, length(one.points) * 0.3) && fits(one.view, one.habitat, cut)) {
      return { ...one, points: cut };
    }
    // Too short to walk: it stays put in the middle of the clear stretch, at
    // home on its own bit of bank or water, doing its idles there -- if that
    // spot is its own kind of ground. Otherwise it stays home.
    const stay = trail[Math.round((best[0] + best[1]) / 2)]!.p;
    const still = [stay, { x: stay.x + 1, y: stay.y }];
    if (!fits(one.view, one.habitat, still)) return { enabled: false, name: one.name, index: one.index } as Arranged;
    return { ...one, points: still };
  });

  // No two residents stop in the same place. The later one's end is pulled
  // in first; when that leaves it too short, the earlier one's is.
  // Copies: `arranged` is memoised by the caller and must not change.
  const live = result
    .filter((one): one is Placed => one.enabled && out(one.index) && one.habitat !== 'sky')
    .map((one) => ({ ...one }));
  for (let pass = 0; pass < 3; pass++) {
    let moved = false;
    for (let j = 0; j < live.length; j++) {
      for (let i = 0; i < j; i++) {
        const a = live[i]!, b = live[j]!;
        for (const bEnd of ['start', 'end'] as const) {
          for (const aEnd of ['start', 'end'] as const) {
            const bp = end(b.points, bEnd), ap = end(a.points, aEnd);
            if (!piled(body(bp, b.paintedHeight), body(ap, a.paintedHeight))) continue;
            const clearOfA = (p: Point) => !piled(body(p, b.paintedHeight), body(end(a.points, aEnd), a.paintedHeight));
            const nb = pullIn(b, bEnd, clearOfA);
            if (nb) { b.points = nb; moved = true; continue; }
            const clearOfB = (p: Point) => !piled(body(p, a.paintedHeight), body(end(b.points, bEnd), b.paintedHeight));
            const na = pullIn(a, aEnd, clearOfB);
            if (na) { a.points = na; moved = true; }
          }
        }
      }
    }
    if (!moved) break;
  }
  // Two that still share a resting place, on routes too short to give way:
  // the later one stays home.
  const home = new Set<number>();
  for (let j = 0; j < live.length; j++) {
    for (let i = 0; i < j; i++) {
      const a = live[i]!, b = live[j]!;
      if (home.has(a.index)) continue;
      const clash = (['start', 'end'] as const).some((bEnd) => (['start', 'end'] as const).some((aEnd) =>
        piled(body(end(b.points, bEnd), b.paintedHeight), body(end(a.points, aEnd), a.paintedHeight))));
      if (clash) home.add(b.index);
    }
  }
  return result.map((one) => {
    if (home.has(one.index)) return { enabled: false, name: one.name, index: one.index } as Arranged;
    const mine = live.find((l) => l.index === one.index);
    return mine && mine.points !== (one as Placed).points ? mine : one;
  });
}

const end = (points: readonly Point[], which: 'start' | 'end') => (which === 'start' ? points[0]! : points[points.length - 1]!);

/** The part of a path between two samples, as a path. */
function slice(points: readonly Point[], trail: Array<{ p: Point; seg: number }>, a: number, b: number): Point[] | null {
  const cut = [trail[a]!.p, ...points.slice(trail[a]!.seg + 1, trail[b]!.seg + 1), trail[b]!.p]
    .filter((p, i, all) => i === 0 || Math.hypot(p.x - all[i - 1]!.x, p.y - all[i - 1]!.y) > 0.5);
  return cut.length >= 2 ? cut : null;
}

/**
 * One end of a route walked back along itself until `ok`, if it stays long
 * enough -- and ends on its own kind of ground (`fits`), so a meadow animal
 * does not give way by stopping on a boulder.
 */
function pullIn(one: Placed, which: 'start' | 'end', ok: (p: Point) => boolean): Point[] | null {
  const trail = samples(one.points);
  const min = Math.max(one.paintedHeight * 1.2, length(one.points) * 0.3);
  const last = trail.length - 1;
  for (let step = 1; step < last; step++) {
    const k = which === 'end' ? last - step : step;
    if (!ok(trail[k]!.p)) continue;
    const cut = which === 'end' ? slice(one.points, trail, 0, k) : slice(one.points, trail, k, last);
    if (!cut || length(cut) < min) return null;
    if (fits(one.view, one.habitat, cut)) return cut;
  }
  return null;
}
